import { createSelector } from 'reselect';
import get from 'lodash/fp/get';

import getOr from 'lodash/fp/getOr';
import getOrNil from 'utils/getOrNil';
import { formatPhone } from 'utils/textUtils';

const getUserState = get('user');

export const getUser = createSelector(getUserState, get('user'));
export const getUserError = createSelector(getUserState, get('error'));
export const getUserReferralCode = createSelector(getUserState, get('referralCode'));
export const getUserHasReferralCode = createSelector(getUserReferralCode, Boolean);
export const getUserIsLoading = createSelector(getUserState, get('isLoading'));
export const getUserFirstName = createSelector(getUser, getOrNil('', 'first_name'));
export const getUserLastName = createSelector(getUser, getOrNil('', 'last_name'));
export const getUserEmail = createSelector(getUser, get('email'));
export const getTextOptin = createSelector(getUser, getOr(true, 'text_optin'));
export const getTextMarketingOptin = createSelector(getUser, getOr(false, 'text_marketing_optin'));
export const getUserPhone = createSelector(getUser, user => {
  try {
    return formatPhone(user.phone);
  } catch {
    return '';
  }
});
export const getUserPubkey = createSelector(getUser, get('pubkey'));
export const getUserGeolocationGuessedCountry = createSelector(
  getUserState,
  get('geolocationGuessedCountry')
);
export const getUserStylist = createSelector(getUser, get('stylist'));
export const getPrefShipMagazine = createSelector(getUser, getOrNil(false, 'pref_ship_magazine'));
export const getPrefShipPumps = createSelector(getUser, getOrNil(false, 'pref_ship_pumps'));
export const getCustomerOrigin = createSelector(getUser, get('customer_origin'));
export const getUserHasAcceptedTerms = createSelector(
  getUser,
  getOrNil(false, 'has_accepted_terms')
);
export const getReferralBalance = createSelector(getUser, getOrNil(0, 'referral.balance'));
export const getUserProfile = createSelector(getUser, get('profile'));
export const getIsOrderFree = createSelector(getUserProfile, getOrNil(false, 'is_next_order_free'));
export const getHaveOrders = createSelector(getUserProfile, getOrNil(false, 'has_order'));
export const getHaveOrdersWithFormulas = createSelector(
  getUserProfile,
  getOrNil(false, 'has_order_with_formulas')
);
const getHairProfileStatus = createSelector(getUserProfile, get('hair_profile_status'));
export const getHaveCompletedHairProfile = createSelector(
  getHairProfileStatus,
  status => status === 'completed'
);
export const getHaveAnsweredSupplementsQuestion = createSelector(
  getUserProfile,
  get('has_answered_supplements_questions')
);
export const getHaveActiveSubscription = createSelector(
  getUserProfile,
  getOrNil(false, 'has_active_subscription')
);
export const getHaveActiveHaircareSubscription = createSelector(
  getUserProfile,
  getOrNil(false, 'has_active_haircare_subscription')
);
export const getHaveActiveSupplementsSubscription = createSelector(
  getUserProfile,
  getOrNil(false, 'has_active_supplements_subscription')
);
export const getHaveOnHoldSubscription = createSelector(
  getUserProfile,
  getOrNil(false, 'has_onhold_subscription')
);
export const getHaveCanceledSubscription = createSelector(
  getUserProfile,
  getOrNil(false, 'has_canceled_subscription')
);

export const getHaveSubscription = createSelector(
  [getHaveActiveSubscription, getHaveOnHoldSubscription],
  (hasActiveSubscription, hasOnHoldSubscription) => hasActiveSubscription || hasOnHoldSubscription
);

export const getCanSeeFeedback = createSelector(
  getUserProfile,
  getOrNil(false, 'can_see_feedback')
);
export const getCanSubscribe = createSelector(getUserProfile, getOrNil(false, 'can_subscribe'));
export const getUserHasRedeemableGift = createSelector(
  getUserProfile,
  getOrNil(false, 'has_redeemable_gift')
);
export const getHasPaidCoreOrder = createSelector(
  getUserProfile,
  getOrNil(false, 'has_paid_core_order')
);
export const getNextOrderFrom = createSelector(
  getUserProfile,
  getOrNil('consultation', 'next_order_from')
);
export const getHaircareFeedbackOnOrder = createSelector(
  getUserProfile,
  getOrNil(null, 'feedback_on_order')
);
export const getSupplementsFeedbackOnOrder = createSelector(
  getUserProfile,
  getOrNil(null, 'supplements_feedback_on_order')
);
// TODO default getCanSubscribeAtCart to true when backend is ready
export const getCanSubscribeAtCart = createSelector(
  getUserProfile,
  getOrNil(false, 'can_subscribe_at_cart')
);
export const getUserAddresses = createSelector(getUserState, getOrNil([], 'addresses'));
export const getUserAddressesStatus = createSelector(getUserState, get('addressesStatus'));
export const getPaymentMethods = createSelector(getUserState, getOrNil([], 'paymentMethods'));
export const getPaymentMethodsStatus = createSelector(getUserState, get('paymentMethodsStatus'));
export const getRecommendationsStatus = createSelector(getUserState, get('recommendationsStatus'));
export const getUserInitials = createSelector(
  getUserFirstName,
  getUserLastName,
  (firstName, lastName) => (firstName && lastName ? (firstName[0] + lastName[0]).toUpperCase() : '')
);

export const getUserFragrance = createSelector(getUserProfile, getOrNil(null, 'fragrance'));

export const getUserFragranceIsSoldOut = createSelector(
  getUserProfile,
  getOrNil(false, 'is_fragrance_soldout')
);

export const getUserRecommendations = createSelector(getUserState, getOrNil([], 'recommendations'));

const getUserABTestGroup = createSelector(getUserProfile, get('ab_test_group'));

export const getProductFrequencyAbTestVariant = createSelector(
  getUserABTestGroup,
  get('product-frequency-recommendation')
);

export const getSupplementsVariant = createSelector(getUserState, get('supplementsVariant'));
export const getSupplementsVariantSlug = createSelector(getSupplementsVariant, get('slug'));

/* Discount the user is elligible to if he subscribes.
 default is a static 15 rate */
export const getUserSubscriptionDiscount = createSelector(
  getUserProfile,
  getOrNil(15, 'subscription_discount')
);

export const getHasGeneratedLivechat = createSelector(getUserState, get('hasGeneratedLivechat'));
export const getHasOpenedLivechat = createSelector(getUserState, get('hasOpenedLivechat'));

/* indicate whether or not the user will receive supp jars or supp pouches */
export const getMustHaveJars = createSelector(getUserProfile, getOr(true, 'must_have_jars'));
